/**
 * Visualisateur 3D Principal
 * 
 * Composant de rendu 3D avec Three.js selon cahier des charges
 * Performance optimale et réactivité temps réel
 */

import React, { useRef, useEffect, useState, Suspense } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { OrbitControls, Grid, Environment, Stats } from '@react-three/drei';
import * as THREE from 'three';
import { StructureBase } from '../modules/structure-base';
import { StructureBase3DService } from '../modules/structure-base';
import { StructureTraverses } from '../modules/traverses';
import { Traverses3DService } from '../modules/traverses';
import { StructureSoudures } from '../modules/soudures';
import { Soudures3DService } from '../modules/soudures';
import { StructureDoublePoutre } from '../modules/double-poutre/types';
import { DoublePoutre3DService } from '../modules/double-poutre';
import { TraversesDoublePoutre3DService } from '../modules/double-poutre/traverses/services/TraversesDoublePoutre3DService';
import { StructureTraversesDoublePoutre } from '../modules/double-poutre/traverses/types';
import { StructureToit } from '../modules/toit';
import { Toit3DService } from '../modules/toit';
// Supprimer l'import inutilisé TypeForme
// import { TypeForme } from '../modules/structure-base/types';
import { StructureAccessoires, ParametresEclairage } from '../modules/accessoires/types';
import { Accessoires3DService } from '../modules/accessoires/services/Accessoires3DService';
// Remplacer l'import pour Parois (retirer Parois3DService)
import { StructureParois } from '../modules/parois';
// Import pour le module Portes
import { StructurePortes } from '../modules/portes';
// import { CadrePorte3DService } from '../modules/portes/services/CadrePorte3DService';
import { IntersectionUtils } from '../utils/geometrie/IntersectionUtils';

// Styles pour le conteneur
const styles = {
  container: {
    width: '100%',
    height: '100%',
    minHeight: '600px',
    border: '2px solid #404040',
    borderRadius: '8px',
    overflow: 'hidden',
    position: 'relative' as const,
    backgroundColor: '#1a1a1a'
  },
  loading: {
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    fontSize: '18px',
    color: '#b0b0b0'
  },
  controls: {
    position: 'absolute' as const,
    top: '10px',
    right: '10px',
    backgroundColor: 'rgba(45, 45, 45, 0.9)',
    padding: '10px',
    borderRadius: '4px',
    fontSize: '12px',
    color: '#e0e0e0',
    border: '1px solid #495057'
  }
};

interface Visualisateur3DProps {
  structure?: StructureBase;
  traverses?: StructureTraverses;
  soudures?: StructureSoudures;
  doublePoutre?: StructureDoublePoutre;
  traversesDoublePoutre?: StructureTraversesDoublePoutre;
  toit?: StructureToit;
  accessoires?: StructureAccessoires | null;
  parois?: StructureParois | null;
  portes?: StructurePortes | null;
  showGrid?: boolean;
  showStats?: boolean;
}

/**
 * Composant de rendu de la structure 3D
 */
const StructureRenderer: React.FC<{ 
  structure?: StructureBase;
  traverses?: StructureTraverses;
  soudures?: StructureSoudures;
  doublePoutre?: StructureDoublePoutre;
  traversesDoublePoutre?: StructureTraversesDoublePoutre;
  toit?: StructureToit;
  accessoires?: StructureAccessoires | null;
  parois?: StructureParois | null;
  portes?: StructurePortes | null;
}> = ({ structure, traverses, soudures, doublePoutre, traversesDoublePoutre, toit, accessoires, parois, portes }) => {
  const { scene } = useThree();
  const structureRef = useRef<THREE.Group>(null);
  const traversesRef = useRef<THREE.Group>(null);
  const souduresRef = useRef<THREE.Group>(null);
  const doublePoutreRef = useRef<THREE.Group>(null);
  const traversesDoublePoutreRef = useRef<THREE.Group>(null);
  const toitRef = useRef<THREE.Group>(null);
  const accessoiresRef = useRef<THREE.Group>(null);
  const paroisRef = useRef<THREE.Group>(null);
  const portesRef = useRef<THREE.Group>(null);
  const [structureService] = useState(() => new StructureBase3DService(scene));
  const [traversesService] = useState(() => new Traverses3DService());
  const [souduresService] = useState(() => new Soudures3DService(scene));
  const [doublePoutreService] = useState(() => new DoublePoutre3DService());
  const [traversesDoublePoutreService] = useState(() => new TraversesDoublePoutre3DService());
  const [toitService] = useState(() => new Toit3DService());
  const [accessoiresService] = useState(() => new Accessoires3DService(scene));
  // const [cadrePorteService] = useState(() => new CadrePorte3DService());

  // Génération de la géométrie 3D pour la structure de base
  useEffect(() => {
    if (!structure || !structureRef.current) return;

    try {
      // Nettoyage de la géométrie précédente
      while (structureRef.current.children.length > 0) {
        const child = structureRef.current.children[0];
        structureRef.current.remove(child);
        if (child instanceof THREE.Mesh && child.geometry) {
          child.geometry.dispose();
        }
      }
      
      // Génération de la nouvelle structure avec paramètres globaux
      const geometrie = structureService.genererStructure3D(
        structure,
        structure.parametres.materiau,
        structure.parametres.epaisseur
      );

      // Couper les montants de base à l'emplacement des portes (on enlève en dessous de l'ouverture)
      if (portes && portes.parametres?.actif && Array.isArray(portes.elements) && portes.elements.length > 0) {
        const zones = portes.elements
          .map((porte: any) => {
            const coins = porte?.geometrie?.coins;
            if (!coins || coins.length === 0) return null;
            let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity, yMax = -Infinity;
            for (const p of coins) {
              if (p.x < minX) minX = p.x;
              if (p.x > maxX) maxX = p.x;
              if (p.z < minZ) minZ = p.z;
              if (p.z > maxZ) maxZ = p.z;
              if (p.y > yMax) yMax = p.y;
            }
            return { minX, maxX, minZ, maxZ, yMax };
          })
          .filter(Boolean) as { minX: number; maxX: number; minZ: number; maxZ: number; yMax: number }[];

        if (zones.length > 0) {
          structureService.couperMontantsSousPortes(zones, structure.parametres.materiau as any, structure.parametres.epaisseur as any);
        }
      }
      
      // Ajout à la scène
      structureRef.current.add(geometrie);
      
    } catch (error) {
      // Erreur génération 3D structure
    }
  }, [structure, structureService, portes]);

  // Mise à jour de la couleur de la structure de base
  useEffect(() => {
    if (!structure?.parametres.couleur) return;
    
    try {
      // Mise à jour de la couleur de la structure de base
      structureService.mettreAJourCouleur(structure.parametres.couleur);
    } catch (error) {
      // Erreur mise à jour couleur structure
    }
  }, [structure?.parametres.couleur, structureService]);

  // Génération de la géométrie 3D pour les traverses
  useEffect(() => {
    if (!traversesRef.current) return;

    // Nettoyage de la géométrie précédente
    while (traversesRef.current.children.length > 0) {
      const child = traversesRef.current.children[0];
      traversesRef.current.remove(child);
      if (child instanceof THREE.Mesh && child.geometry) {
        child.geometry.dispose();
      }
    }

    // Si pas de traverses, on s'arrête après le nettoyage
    if (!traverses) {
      return;
    }

    try {
      // Génération des nouvelles traverses
      const geometrieTraverses = traversesService.genererTraverses3D(traverses);
      
      // Ajout à la scène
      traversesRef.current.add(geometrieTraverses);
    } catch (error) {
      // Erreur génération 3D traverses
    }
  }, [traverses, traversesService]);

  // Génération de la géométrie 3D pour les soudures
  useEffect(() => {
    if (!souduresRef.current) return;

    // Nettoyage de la géométrie précédente
    while (souduresRef.current.children.length > 0) {
      const child = souduresRef.current.children[0];
      souduresRef.current.remove(child);
      if (child instanceof THREE.Mesh && child.geometry) {
        child.geometry.dispose();
      }
    }

    // Si pas de soudures, on s'arrête après le nettoyage
    if (!soudures) {
      return;
    }

    try {
      // Génération des nouvelles soudures
      const geometrieSoudures = souduresService.genererSoudures3D(soudures);
      
      // Ajout à la scène
      souduresRef.current.add(geometrieSoudures);
    } catch (error) {
      // Erreur génération 3D soudures
    }
  }, [soudures, souduresService]);

  // Génération de la géométrie 3D pour la double poutre
  useEffect(() => {
    if (!doublePoutreRef.current) return;

    // Nettoyage de la géométrie précédente
    while (doublePoutreRef.current.children.length > 0) {
      const child = doublePoutreRef.current.children[0];
      doublePoutreRef.current.remove(child);
      if (child instanceof THREE.Mesh && child.geometry) {
        child.geometry.dispose();
      }
    }

    // Si pas de double poutre, on s'arrête après le nettoyage
    if (!doublePoutre) {
      return;
    }

    try {
      // Génération asynchrone de la structure de double poutre via le service
      const hauteurBase = (structure?.parametres?.hauteur as number | undefined)
        ?? (doublePoutre.poutres?.poutreBasse?.hauteur as number | undefined)
        ?? 2.2;

      (async () => {
        const group = await doublePoutreService.genererStructure(
          doublePoutre.parametres,
          doublePoutre.forme,
          hauteurBase
        );
        if (!doublePoutreRef.current) return;
        // Ajout à la scène
        doublePoutreRef.current.add(group);
      })();
    } catch (error) {
      // Erreur génération 3D double poutre
    }
  }, [doublePoutre, doublePoutreService, structure?.parametres?.hauteur]);

  // Génération de la géométrie 3D pour les traverses de double poutre
  useEffect(() => {
    if (!traversesDoublePoutreRef.current) return;

    // Nettoyage de la géométrie précédente
    while (traversesDoublePoutreRef.current.children.length > 0) {
      const child = traversesDoublePoutreRef.current.children[0];
      traversesDoublePoutreRef.current.remove(child);
      if (child instanceof THREE.Mesh && child.geometry) {
        child.geometry.dispose();
      }
    }

    // Si pas de traverses de double poutre, on s'arrête après le nettoyage
    if (!traversesDoublePoutre) {
      return;
    }

    try {
      // Génération des nouvelles traverses de double poutre
      const geometrieTraversesDoublePoutre = traversesDoublePoutreService.genererTraverses3D(traversesDoublePoutre);
      
      // Ajout à la scène
      traversesDoublePoutreRef.current.add(geometrieTraversesDoublePoutre);
    } catch (error) {
      // Erreur génération 3D traverses double poutre
    }
  }, [traversesDoublePoutre, traversesDoublePoutreService]);

  // Génération de la géométrie 3D pour le toit
  useEffect(() => {
    if (!toitRef.current) return;

    // Nettoyage de la géométrie précédente
    while (toitRef.current.children.length > 0) {
      const child = toitRef.current.children[0];
      toitRef.current.remove(child);
      if (child instanceof THREE.Mesh && child.geometry) {
        child.geometry.dispose();
      }
    }

    // Si pas de toit, on s'arrête après le nettoyage
    if (!toit) {
      return;
    }

    try {
       // Génération du nouveau toit
       const geometrieToit = toitService.genererToit3D(toit);
       
       // Ajout à la scène
       toitRef.current.add(geometrieToit);

       // Après ajout du toit, s'assurer que la lampe centrale est bien re-parentée sous le sommet (point de convergence)
       if (accessoiresRef.current) {
         const lampeDansAccessoires = accessoiresRef.current.getObjectByName('LampeCentrale3D');
         if (lampeDansAccessoires && toitRef.current) {
           const apex = toitRef.current.getObjectByName('jonction_toit') || toitRef.current;
           // Conserver la transformée monde lors du changement de parent
           apex.attach(lampeDansAccessoires);
         }
       }
       
     } catch (error) {
       console.error('Erreur génération 3D toit:', error);
     }
  }, [toit, toitService]);

  // Mise à jour de la couleur du toit quand la couleur de la structure change
  useEffect(() => {
    if (!structure?.parametres.couleur) return;

    try {
      toitService.mettreAJourCouleur(structure.parametres.couleur);
    } catch (error) {
      // Erreur mise à jour couleur toit
    }
  }, [structure?.parametres.couleur, toitService]);

  // Génération de la géométrie 3D pour les accessoires (figure humaine, mobilier, végétation)
  useEffect(() => {
    if (!accessoiresRef.current) return;

    // Nettoyage des accessoires précédents
    while (accessoiresRef.current.children.length > 0) {
      const child = accessoiresRef.current.children[0];
      accessoiresRef.current.remove(child);
      if ((child as any as THREE.Mesh).isMesh && (child as any as THREE.Mesh).geometry) {
        (child as any as THREE.Mesh).geometry.dispose();
      }
    }

    // Si pas d'accessoires ou module inactif, on s'arrête après le nettoyage
    if (!accessoires || !accessoires.parametres?.actif) {
      return;
    }

    try {
      const group = accessoiresService.genererAccessoires3D(accessoires);
      accessoiresRef.current.add(group);

      // Re-parentage: la lampe centrale doit appartenir au sommet du toit (point de convergence)
      const lampe = group.getObjectByName('LampeCentrale3D');
      if (lampe && toitRef.current) {
        const apex = toitRef.current.getObjectByName('jonction_toit') || toitRef.current;
        // Conserver la transformée monde lors du changement de parent
        apex.attach(lampe);
      }
    } catch (error) {
      // Erreur génération 3D accessoires
    }
  }, [
    accessoires, 
    accessoiresService,
    // Dépendances spécifiques pour les éclairages 3D
    accessoires?.parametres?.eclairage?.actif,
    accessoires?.parametres?.eclairage?.lampeCentrale?.actif,
    accessoires?.parametres?.eclairage?.lampeCentrale?.intensite,
    accessoires?.parametres?.eclairage?.lampeCentrale?.couleur,
    accessoires?.parametres?.eclairage?.barresLEDVerticales?.actif,
    accessoires?.parametres?.eclairage?.barresLEDHorizontales?.actif,
    accessoires?.parametres?.eclairage?.spotsDirectionnels?.actif,
    accessoires?.parametres?.eclairage?.guirlandeLED?.actif
  ]);

  // Génération 3D pour les parois
  useEffect(() => {
    // Désactivé: le rendu des parois est désormais géré par l'effet suivant avec découpe autour des portes
    return;

    // if (!paroisRef.current) return;

    // // Nettoyage
    // while (paroisRef.current.children.length > 0) {
    //   const child = paroisRef.current.children[0];
    //   paroisRef.current.remove(child);
    //   if (child instanceof THREE.Mesh && child.geometry) {
    //     child.geometry.dispose();
    //   }
    // }

    // if (!parois || !parois.parametres?.actif) {
    //   return;
    // }

    // try {
    //   // Filtrer les panneaux qui n'intersectent pas avec des portes (pour éviter le chevauchement visuel)
    //   let panneauxSource = parois.panneaux;
    //   if (portes && portes.parametres?.actif && Array.isArray(portes.elements) && portes.elements.length > 0) {
    //     panneauxSource = IntersectionUtils.filtrerPanneauxSansIntersection(parois.panneaux as any, portes.elements as any) as any;
    //   }

    //   for (const panneau of panneauxSource) {
    //     const geometry = new THREE.BufferGeometry();
    //     const vertices = new Float32Array(panneau.vertices);
    //     const indices = new Uint16Array(panneau.indices);
    //     geometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
    //     geometry.setIndex(new THREE.BufferAttribute(indices, 1));
    //     geometry.computeVertexNormals();

    //     const material = new THREE.MeshStandardMaterial({
    //       color: new THREE.Color(panneau.materiaux.diffuse),
    //       transparent: panneau.materiaux.transparency > 0,
    //       opacity: Math.max(0, Math.min(1, 1 - panneau.materiaux.transparency)),
    //       metalness: 0.1,
    //       roughness: 0.8,
    //       side: THREE.DoubleSide
    //     });

    //     const mesh = new THREE.Mesh(geometry, material);
    //     mesh.castShadow = true;
    //     mesh.receiveShadow = true;
    //     paroisRef.current.add(mesh);
    //   }
    // } catch (error) {
    //   // Erreur génération 3D parois
    // }
  }, [parois]);

  // Génération 3D pour les portes
  useEffect(() => {
    if (!portesRef.current) return;

    // Nettoyage
    while (portesRef.current.children.length > 0) {
      const child = portesRef.current.children[0];
      portesRef.current.remove(child);
      if (child instanceof THREE.Mesh && child.geometry) {
        child.geometry.dispose();
      }
    }

    if (!portes || !portes.parametres?.actif) {
      return;
    }

    try {
      // Préparer infos matériau/épaisseur pour les montants
      const epaisseurM = ((structure?.parametres.epaisseur as number) || 40) / 1000; // mm -> m
      const estCylindrique = (structure?.parametres.materiau as any) === 'fer_a_beton';
      const couleurPorte = structure?.parametres.couleur || '#8B4513';

      for (const porte of portes.elements) {
        // Rendu uniforme des portes selon vertices/indices
        const geometry = new THREE.BufferGeometry();
        const vertices = new Float32Array(porte.vertices);
        const indices = new Uint16Array(porte.indices);
        geometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
        geometry.setIndex(new THREE.BufferAttribute(indices, 1));
        geometry.computeVertexNormals();

        const material = new THREE.MeshStandardMaterial({
          color: new THREE.Color(couleurPorte),
          transparent: porte.materiaux.transparency > 0,
          opacity: Math.max(0, Math.min(1, 1 - porte.materiaux.transparency)),
          metalness: 0.1,
          roughness: 0.8,
          side: THREE.DoubleSide
        });

        const mesh = new THREE.Mesh(geometry, material);
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        portesRef.current.add(mesh);

        // Ajout des montants verticaux de chaque côté de l'ouverture
        const coins = porte.geometrie?.coins || [];
        if (Array.isArray(coins) && coins.length >= 6 && coins.length % 2 === 0) {
          const ring = coins.length / 2; // points du bas et du haut
          const leftBas = coins[0];
          const leftHautTheorique = coins[ring]; // sommet actuel de l'ouverture
          const rightBas = coins[ring - 1];
          const rightHautTheorique = coins[coins.length - 1];

          // La hauteur cible doit aller jusqu'à la poutre haute (hauteur structure)
          const hauteurStructure = (structure?.parametres.hauteur as number) || (leftHautTheorique.y - leftBas.y);
          const leftHaut = { x: leftBas.x, y: leftBas.y + hauteurStructure, z: leftBas.z };
          const rightHaut = { x: rightBas.x, y: rightBas.y + hauteurStructure, z: rightBas.z };

          // Helper pour créer un montant vertical entre deux points (aligné Y)
          const creerMontantVertical = (bas: { x: number; y: number; z: number }, haut: { x: number; y: number; z: number }) => {
            const hauteur = Math.max(0.001, haut.y - bas.y);
            let geom: THREE.BufferGeometry;
            if (estCylindrique) {
              geom = new THREE.CylinderGeometry(epaisseurM / 2, epaisseurM / 2, hauteur, 8);
            } else {
              geom = new THREE.BoxGeometry(epaisseurM, hauteur, epaisseurM);
            }
            const centre = new THREE.Vector3(bas.x, (bas.y + haut.y) / 2, bas.z);
            const matrix = new THREE.Matrix4();
            matrix.makeTranslation(centre.x, centre.y, centre.z);
            geom.applyMatrix4(matrix);
            return geom;
          };

          const montantGaucheGeom = creerMontantVertical(leftBas, leftHaut);
          const montantDroitGeom = creerMontantVertical(rightBas, rightHaut);

          const matMontant = new THREE.MeshStandardMaterial({
            color: new THREE.Color(couleurPorte),
            metalness: 0.2,
            roughness: 0.7
          });

          const montantGaucheMesh = new THREE.Mesh(montantGaucheGeom, matMontant);
          montantGaucheMesh.castShadow = true;
          montantGaucheMesh.receiveShadow = true;
          portesRef.current.add(montantGaucheMesh);

          const montantDroitMesh = new THREE.Mesh(montantDroitGeom, matMontant);
          montantDroitMesh.castShadow = true;
          montantDroitMesh.receiveShadow = true;
          portesRef.current.add(montantDroitMesh);
        }
      }
    } catch (error) {
      // Erreur génération 3D portes
    }
  }, [portes, structure]);

  // Génération 3D pour les parois avec découpe
  useEffect(() => {
    if (!paroisRef.current) return;

    // Nettoyage
    while (paroisRef.current.children.length > 0) {
      const child = paroisRef.current.children[0];
      paroisRef.current.remove(child);
      if (child instanceof THREE.Mesh && child.geometry) {
        child.geometry.dispose();
      }
    }

    if (!parois || !parois.parametres?.actif) {
      return;
    }

    // Helpers géométriques locaux
    const eps = 1e-6;
    type Coin = { x: number; y: number; z: number };

    const creerMeshDepuisCoins = (coins: Coin[], matColor: string, transparency: number) => {
      const verts = new Float32Array([
        coins[0].x, coins[0].y, coins[0].z,
        coins[1].x, coins[1].y, coins[1].z,
        coins[2].x, coins[2].y, coins[2].z,
        coins[3].x, coins[3].y, coins[3].z,
      ]);
      const idx = new Uint16Array([0,1,2, 2,3,0]);
      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', new THREE.BufferAttribute(verts, 3));
      geometry.setIndex(new THREE.BufferAttribute(idx, 1));
      geometry.computeVertexNormals();
      const material = new THREE.MeshStandardMaterial({
        color: new THREE.Color(matColor),
        transparent: transparency > 0,
        opacity: Math.max(0, Math.min(1, 1 - transparency)),
        metalness: 0.1,
        roughness: 0.8,
        side: THREE.DoubleSide
      });
      const mesh = new THREE.Mesh(geometry, material);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      return mesh;
    };

    const addMesh = (mesh: THREE.Mesh) => {
      paroisRef.current!.add(mesh);
    };

    const construireCoinsPanneau = (
      axis: 'x'|'z',
      a: {x:number;z:number},
      b: {x:number;z:number},
      vdx: number,
      vdz: number,
      u1: number,
      u2: number,
      y0: number,
      y1: number
    ): Coin[] => {
      // u correspond à x si axis==='x', sinon à z
      if (axis === 'x') {
        const z1 = vdx !== 0 ? a.z + (u1 - a.x) * (vdz / vdx) : a.z;
        const z2 = vdx !== 0 ? a.z + (u2 - a.x) * (vdz / vdx) : b.z;
        return [
          { x: u1, y: y0, z: z1 },
          { x: u2, y: y0, z: z2 },
          { x: u2, y: y1, z: z2 },
          { x: u1, y: y1, z: z1 },
        ];
      } else {
        const x1 = vdz !== 0 ? a.x + (u1 - a.z) * (vdx / vdz) : a.x;
        const x2 = vdz !== 0 ? a.x + (u2 - a.z) * (vdx / vdz) : b.x;
        return [
          { x: x1, y: y0, z: u1 },
          { x: x2, y: y0, z: u2 },
          { x: x2, y: y1, z: u2 },
          { x: x1, y: y1, z: u1 },
        ];
      }
    };

    try {
      const portesList = (portes && portes.parametres?.actif) ? (portes.elements || []) : [];

      for (const panneau of parois.panneaux) {
        const coins: Coin[] = panneau.geometrie.coins as any;
        if (!coins || coins.length < 4) continue;

        // Déterminer bord bas du panneau (dans Parois3DService: [a(y0), b(y0), b(y1), a(y1)])
        const a = { x: coins[0].x, z: coins[0].z };
        const b = { x: coins[1].x, z: coins[1].z };
        const vdx = b.x - a.x;
        const vdz = b.z - a.z;
        const axis: 'x'|'z' = Math.abs(vdx) >= Math.abs(vdz) ? 'x' : 'z';

        const pY0 = Math.min(coins[0].y, coins[1].y, coins[2].y, coins[3].y);
        const pY1 = Math.max(coins[0].y, coins[1].y, coins[2].y, coins[3].y);

        const pMin = axis === 'x' ? Math.min(coins[0].x, coins[1].x) : Math.min(coins[0].z, coins[1].z);
        const pMax = axis === 'x' ? Math.max(coins[0].x, coins[1].x) : Math.max(coins[0].z, coins[1].z);

        // Trouver une porte intersectante (première) pour découpe
        const porteIntersect = portesList.find((porte: any) => IntersectionUtils.detecterIntersectionPorteParoi(porte, panneau as any));

        if (!porteIntersect) {
          // Pas d'intersection: rendre le panneau tel quel
          const mesh = creerMeshDepuisCoins(coins, panneau.materiaux.diffuse, panneau.materiaux.transparency);
          addMesh(mesh);
          continue;
        }

        // Calcul de la zone de découpe à partir de la porte
        const pc = porteIntersect.geometrie.coins as Coin[];
        const dY0 = Math.min(pc[0].y, pc[1].y, pc[2].y, pc[3].y);
        const dY1 = Math.max(pc[0].y, pc[1].y, pc[2].y, pc[3].y);

        const dMin = axis === 'x' ? Math.min(pc[0].x, pc[1].x) : Math.min(pc[0].z, pc[1].z);
        const dMax = axis === 'x' ? Math.max(pc[0].x, pc[1].x) : Math.max(pc[0].z, pc[1].z);

        // Clamp porte à l'intérieur du panneau
        const cutMin = Math.max(pMin, dMin);
        const cutMax = Math.min(pMax, dMax);
        const cutY0 = Math.max(pY0, dY0);
        const cutY1 = Math.min(pY1, dY1);

        // Si la découpe est invalide, rendre le panneau tel quel
        if (!(cutMax - cutMin > eps && cutY1 - cutY0 > eps)) {
          const mesh = creerMeshDepuisCoins(coins, panneau.materiaux.diffuse, panneau.materiaux.transparency);
          addMesh(mesh);
          continue;
        }

        // Générer sous-panneaux: gauche, droite, bas, haut
        const sousPanneaux: Coin[][] = [];

        // Gauche
        if (cutMin - pMin > eps) {
          sousPanneaux.push(construireCoinsPanneau(axis, a, b, vdx, vdz, pMin, cutMin, pY0, pY1));
        }
        // Droite
        if (pMax - cutMax > eps) {
          sousPanneaux.push(construireCoinsPanneau(axis, a, b, vdx, vdz, cutMax, pMax, pY0, pY1));
        }
        // Bas (zone en-dessous de la porte sur la largeur de la porte)
        if (cutY0 - pY0 > eps) {
          sousPanneaux.push(construireCoinsPanneau(axis, a, b, vdx, vdz, cutMin, cutMax, pY0, cutY0));
        }
        // Haut (zone au-dessus de la porte sur la largeur de la porte)
        if (pY1 - cutY1 > eps) {
          sousPanneaux.push(construireCoinsPanneau(axis, a, b, vdx, vdz, cutMin, cutMax, cutY1, pY1));
        }

        // Rendu des sous-panneaux
        for (const sp of sousPanneaux) {
          const mesh = creerMeshDepuisCoins(sp, panneau.materiaux.diffuse, panneau.materiaux.transparency);
          addMesh(mesh);
        }
      }
    } catch (error) {
      // Erreur génération 3D parois
    }
  }, [parois, portes]);

  // Cube de test si aucune structure n'est fournie
  const TestCube: React.FC = () => {
    return (
      <mesh position={[0, 1, 0]} castShadow receiveShadow>
        <boxGeometry args={[2, 2, 2]} />
        <meshStandardMaterial color="#4dabf7" />
      </mesh>
    );
  };
  
  // Afficher le cube seulement si pas de structure OU si structure avec erreur
  const shouldShowTestCube = !structure || !!structure.error;

  return (
    <>
      <group ref={structureRef} />
      <group ref={traversesRef} />
      <group ref={souduresRef} />
      <group ref={doublePoutreRef} />
      <group ref={traversesDoublePoutreRef} />
      <group ref={toitRef} />
      <group ref={accessoiresRef} />
      <group ref={paroisRef} />
      <group ref={portesRef} />
      {shouldShowTestCube && <TestCube />}
    </>
  );
};

/**
 * Contrôles de caméra optimisés
 */
const CameraControls: React.FC = () => {
  const { camera, gl } = useThree();
  const controlsRef = useRef<any>(null);

  useEffect(() => {
    if (controlsRef.current) {
      // Configuration optimisée des contrôles
      controlsRef.current.enableDamping = true;
      controlsRef.current.dampingFactor = 0.05;
      controlsRef.current.enableZoom = true;
      controlsRef.current.enablePan = true;
      controlsRef.current.enableRotate = true;
      controlsRef.current.maxPolarAngle = Math.PI * 0.75;
      controlsRef.current.minDistance = 2;
      controlsRef.current.maxDistance = 50;
    }
  }, []);

  useFrame(() => {
    if (controlsRef.current) {
      controlsRef.current.update();
    }
  });

  return (
    <OrbitControls
      ref={controlsRef}
      target={[0, 1, 0]}
    />
  );
};

/**
 * Éclairage optimisé pour la visualisation
 * Seuls les éclairages 3D spécifiques sont gérés par le service Accessoires3D
 */
const SceneLighting: React.FC<{ eclairage?: ParametresEclairage | null }> = ({ eclairage }) => {
  const { gl, scene } = useThree();

  // Si l'éclairage spécifique est désactivé, on force une ambiance de jour naturelle
  const aRaw = typeof eclairage?.ambiance === 'number' ? eclairage!.ambiance : (eclairage?.ambiance === 'nuit' ? 0 : eclairage?.ambiance === 'crepuscule' ? 0.5 : 1);
  
  // Mappage correct : 0=Nuit noire (gauche), 1=Plein jour (droite)
  let a: number;
  if (eclairage?.actif === false) {
    a = 0.5; // Jour naturel par défaut
  } else {
    // Ne pas forcer des valeurs de repli aux extrêmes; utiliser directement la valeur saisie
    a = aRaw;
  }
  
  // Mappage naturel : 0=Nuit, 1=Plein jour
  const timeOfDay = a; // 0..1 (0 = nuit, 1 = plein jour)
  
  // Flags pour cas extrêmes (uniquement extrême gauche = nuit noire)
  const isExtremeDark = a <= 0.01;
  
  // Logique normale pour les valeurs intermédiaires (0.01 < a < 0.99)
  const solarIntensity = Math.sin(timeOfDay * Math.PI) * 0.85 + 0.15; // De 0.15 (matin/soir) à 1.0 (midi)
  
  // Position du soleil réaliste (arc de cercle dans le ciel)
  const sunAngle = timeOfDay * Math.PI; // 0 à π
  const sunHeight = Math.sin(sunAngle) * 15 + 2; // Hauteur variable du soleil
  const sunX = Math.cos(sunAngle) * 20; // Position horizontale
  const sunZ = 5; // Position fixe en profondeur
  
  // Couleurs et intensités réalistes selon l'heure (midi plus neutre, pas bleu)
  const matin = {
    background: new THREE.Color('#2a1810'),
    exposure: 0.6,
    ambient: 0.02,
    hemiSky: new THREE.Color('#332211'),
    hemiGround: new THREE.Color('#110a05'),
    hemiIntensity: 0.08,
    dir: 0.1,
    sunColor: new THREE.Color('#ffaa66'),
    fogNear: 12,
    fogFar: 60,
  };
  
  const midi = {
    background: new THREE.Color('#f2efe6'), // Teinte neutre chaude au lieu de bleu saturé
    exposure: 1.0,
    ambient: 0.35,
    hemiSky: new THREE.Color('#ffffff'),
    hemiGround: new THREE.Color('#f8f5ec'),
    hemiIntensity: 0.7,
    dir: 1.2,
    sunColor: new THREE.Color('#fff3e0'),
    fogNear: null as number | null,
    fogFar: null as number | null,
  };
  
  const soir = {
    background: new THREE.Color('#1a0f08'),
    exposure: 0.5,
    ambient: 0.015,
    hemiSky: new THREE.Color('#2d1808'),
    hemiGround: new THREE.Color('#0f0703'),
    hemiIntensity: 0.06,
    dir: 0.08,
    sunColor: new THREE.Color('#ff6633'),
    fogNear: 10,
    fogFar: 50,
  };

  // Fonctions d'interpolation
  const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
  const lerpColor = (a: THREE.Color, b: THREE.Color, t: number) => a.clone().lerp(b, t);

  // Types forts pour les réglages d'éclairage interpolés
  type LightingSettings = {
    background: string;
    exposure: number;
    ambient: number;
    hemiSky: string;
    hemiGround: string;
    hemiIntensity: number;
    dir: number;
    sunColor: string;
    sunPosition: [number, number, number];
    fogNear: number | null;
    fogFar: number | null;
  };

  // Calculer les paramètres selon le moment de la journée (valable pour le mode normal)
  let settings: LightingSettings;
  if (timeOfDay < 0.5) {
    // Transition Matin -> Midi (0.0 à 0.5)
    const t = timeOfDay * 2; // normaliser entre 0 et 1
    settings = {
      background: lerpColor(matin.background, midi.background, t).getStyle(),
      exposure: lerp(matin.exposure, midi.exposure, t),
      ambient: lerp(matin.ambient, midi.ambient, t),
      hemiSky: lerpColor(matin.hemiSky, midi.hemiSky, t).getStyle(),
      hemiGround: lerpColor(matin.hemiGround, midi.hemiGround, t).getStyle(),
      hemiIntensity: lerp(matin.hemiIntensity, midi.hemiIntensity, t),
      dir: lerp(matin.dir, midi.dir, t),
      sunColor: lerpColor(matin.sunColor, midi.sunColor, t).getStyle(),
      sunPosition: [sunX, sunHeight, sunZ],
      fogNear: t > 0.7 ? null : lerp(matin.fogNear, 30, t),
      fogFar: t > 0.7 ? null : lerp(matin.fogFar, 100, t),
    };
  } else {
    // Transition Midi -> Soir (0.5 à 1.0)
    const t = (timeOfDay - 0.5) * 2; // normaliser entre 0 et 1
    settings = {
      background: lerpColor(midi.background, soir.background, t).getStyle(),
      exposure: lerp(midi.exposure, soir.exposure, t),
      ambient: lerp(midi.ambient, soir.ambient, t),
      hemiSky: lerpColor(midi.hemiSky, soir.hemiSky, t).getStyle(),
      hemiGround: lerpColor(midi.hemiGround, soir.hemiGround, t).getStyle(),
      hemiIntensity: lerp(midi.hemiIntensity, soir.hemiIntensity, t),
      dir: lerp(midi.dir, soir.dir, t),
      sunColor: lerpColor(midi.sunColor, soir.sunColor, t).getStyle(),
      sunPosition: [sunX, sunHeight, sunZ],
      fogNear: t > 0.3 ? lerp(20, soir.fogNear, (t - 0.3) / 0.7) : null,
      fogFar: t > 0.3 ? lerp(80, soir.fogFar, (t - 0.3) / 0.7) : null,
    };
  }

  // Sélection des paramètres de scène à appliquer (un seul useEffect non conditionnel)
  const desiredBackground = isExtremeDark ? '#000000' : settings.background;
  const desiredExposure = isExtremeDark ? 0.25 : settings.exposure;
  const desiredFogNear = isExtremeDark ? 1 : settings.fogNear;
  const desiredFogFar = isExtremeDark ? 10 : settings.fogFar;

  useEffect(() => {
    scene.background = new THREE.Color(desiredBackground as any);
    (gl as any).toneMappingExposure = desiredExposure;
    if (desiredFogNear !== null && desiredFogFar !== null) {
      scene.fog = new THREE.Fog(desiredBackground as any, desiredFogNear as number, desiredFogFar as number);
    } else {
      scene.fog = null as any;
    }
  }, [gl, scene, desiredBackground, desiredExposure, desiredFogNear, desiredFogFar]);

  // Rendu des lumières selon le mode
  if (isExtremeDark) {
    return (
      <>
        <ambientLight intensity={0.001} />
      </>
    );
  }

  return (
    <>
      <ambientLight intensity={settings.ambient} />
      <hemisphereLight args={[settings.hemiSky as any, settings.hemiGround as any, settings.hemiIntensity]} />
      <directionalLight position={[10, 10, 5]} intensity={settings.dir} />
    </>
  );
};

/**
 * Composant principal du visualisateur 3D
 */
export const Visualisateur3D: React.FC<Visualisateur3DProps> = ({
  structure,
  traverses,
  soudures,
  doublePoutre,
  traversesDoublePoutre,
  toit,
  accessoires,
  parois,
  portes,
  showGrid = true,
  showStats = false
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Gestion du chargement
  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 1000);
    return () => clearTimeout(timer);
  }, []);

  // Choix dynamique de l'environnement HDRI selon l'ambiance
  const ambiance = accessoires?.parametres.eclairage?.ambiance;
  const eclairageActif = accessoires?.parametres.eclairage?.actif;
  const ambNumRaw = typeof ambiance === 'number' ? ambiance : (ambiance === 'nuit' ? 0 : ambiance === 'crepuscule' ? 0.5 : 1);
  const ambNum = eclairageActif === false ? 0.5 : ambNumRaw;
  // Mappage correct selon l'ambiance : 0=nuit noire (gauche), 1=plein jour (droite)
  const environmentPreset = eclairageActif === false ? 'sunset' : (ambNum < 0.2 ? 'night' : ambNum < 0.6 ? 'sunset' : 'park');
  const _hdrMap: Record<string, string> = { sunset: 'venice_sunset_1k.hdr', night: 'dikhololo_night_1k.hdr', park: 'rooitou_park_1k.hdr' };
  const environmentFile = `${process.env.PUBLIC_URL || ''}/hdri/${_hdrMap[environmentPreset]}`;
  const extremeDark = eclairageActif === false ? false : (ambNum <= 0.01);

  return (
    <div style={styles.container}>
      {isLoading && (
        <div style={styles.loading}>
          🔄 Initialisation du moteur 3D...
        </div>
      )}
      
      <div style={styles.controls}>
        🖱️ Clic gauche: Rotation | Molette: Zoom | Clic droit: Panoramique
      </div>

      <Canvas
        shadows
        camera={{
          position: [8, 6, 8],
          fov: 60,
          near: 0.1,
          far: 1000
        }}
        gl={{
          antialias: true,
          alpha: true
        }}
        onCreated={({ gl }) => {
          gl.setClearColor(0x1a1a1a, 1);
          gl.shadowMap.enabled = true;
          gl.shadowMap.type = THREE.PCFSoftShadowMap;
        }}
      >
        <Suspense fallback={null}>
          {/* Éclairage de la scène */}
          <SceneLighting eclairage={accessoires?.parametres.eclairage} />
          
          {/* Environnement */}
          {!extremeDark && (
            <Environment files={environmentFile} background={false} />
          )}
          
          {/* Grille de référence */}
          {showGrid && (
            <Grid
              args={[20, 20]}
              cellSize={1}
              cellThickness={0.5}
              cellColor={0xcccccc}
              sectionSize={5}
              sectionThickness={1}
              sectionColor={0x999999}
              fadeDistance={25}
              fadeStrength={1}
              followCamera={false}
              infiniteGrid={false}
            />
          )}
          
          {/* Plan de sol */}
          <mesh
            rotation={[-Math.PI / 2, 0, 0]}
            position={[0, -0.01, 0]}
            receiveShadow
          >
            <planeGeometry args={[50, 50]} />
            {/* Sol plus discret */}
            <meshLambertMaterial color={0xddd8c3} transparent opacity={0.2} />
          </mesh>
          
          {/* Rendu de la structure, des traverses, des soudures et du toit */}
          <StructureRenderer 
            structure={structure} 
            traverses={traverses} 
            soudures={soudures} 
            doublePoutre={doublePoutre} 
            traversesDoublePoutre={traversesDoublePoutre} 
            toit={toit} 
            accessoires={accessoires} 
            parois={parois} 
            portes={portes}
          />
          
          {/* Contrôles de caméra */}
          <CameraControls />
          
          {/* Statistiques de performance */}
          {showStats && <Stats />}
        </Suspense>
      </Canvas>
      
      {error && (
        <div style={{ ...styles.loading, color: '#dc3545' }}>
          ❌ Erreur: {error}
        </div>
      )}
    </div>
  );
};

export default Visualisateur3D;
